import { ChevronLeft, ChevronRight } from "lucide-react-native";
import { ComponentRef, FC, useRef, useState } from "react";
import { View } from "react-native";

import { Box, Center, HStack, Pressable, Text } from "@/components/ui";
import { useLocalization } from "@/hooks";

export interface HeatmapCell {
  column: number;
  count: number;
  row: number;
}

interface HeatmapProps {
  cells: HeatmapCell[];
  columnLabels: string[];
  rowLabels: string[];
  totalRespondents?: number;
}

interface TooltipData {
  count: number;
  label: string;
  percentage: string;
  x: number;
  y: number;
}

const CELL_GAP = 3;
const CELL_RADIUS = 8;
const LABEL_WIDTH = 150;
const HEADER_HEIGHT_LARGE = 90;
const HEADER_HEIGHT_SMALL = 36;
const SHORT_LABEL_THRESHOLD = 4;
const PAGE_SIZE = 6;

const getColor = (count: number, maxCount: number): string => {
  if (count === 0) return "transparent";
  const intensity = Math.sqrt(count / maxCount);
  const r = Math.round(245 - intensity * 120);
  const g = Math.round(240 - intensity * 148);
  const b = Math.round(255 - intensity * 30);
  return `rgb(${r}, ${g}, ${b})`;
};

const getTextColor = (count: number, maxCount: number): string => {
  if (count === 0) return "rgb(var(--color-typography-400))";
  const intensity = Math.sqrt(count / maxCount);
  return intensity > 0.55
    ? "rgb(var(--color-typography-0))"
    : "rgb(var(--color-typography-700))";
};

function HeatmapTooltip({ data }: { data: TooltipData | null }) {
  if (!data) return null;
  return (
    <div
      style={{
        background: "white",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
        fontFamily: "inherit",
        left: data.x,
        padding: "8px 12px",
        pointerEvents: "none",
        position: "absolute",
        top: data.y,
        transform: "translate(-50%, -100%)",
        marginTop: -8,
        whiteSpace: "nowrap",
        zIndex: 50,
      }}
    >
      <p
        style={{
          color: "hsl(222,20%,16%)",
          fontSize: 12,
          fontWeight: 600,
          margin: 0,
        }}
      >
        {data.label}
      </p>
      <p
        style={{
          color: "hsl(260,60%,50%)",
          fontSize: 12,
          margin: "2px 0 0",
        }}
      >
        {data.percentage} · N={data.count}
      </p>
    </div>
  );
}

const Heatmap: FC<HeatmapProps> = ({
  cells,
  columnLabels,
  rowLabels,
  totalRespondents,
}) => {
  const { i18n } = useLocalization();
  const containerRef = useRef<ComponentRef<typeof View>>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [hoveredCell, setHoveredCell] = useState<{
    row: number;
    col: number;
  } | null>(null);
  const [page, setPage] = useState(0);

  if (
    cells.length === 0 ||
    rowLabels.length === 0 ||
    columnLabels.length === 0
  ) {
    return (
      <Center className="h-[300px] w-full">
        <Text className="text-typography-500">
          {i18n.t("dashboardNavigator.noData")}
        </Text>
      </Center>
    );
  }

  const shortLabels = columnLabels.every(
    (l) => l.length <= SHORT_LABEL_THRESHOLD
  );
  // The header hosts the (rotated, up to 2-line) column labels. Rather than a
  // flat `HEADER_HEIGHT_LARGE`, scale to the longest label so medium labels
  // (e.g. "Believable") don't reserve the full tall header — that surplus top
  // space is what threw off the heatmap's vertical centering. Clamped between
  // the small header and the large one.
  const maxColLabelLen = columnLabels.reduce(
    (max, label) => Math.max(max, label.length),
    0
  );
  const headerHeight = shortLabels
    ? HEADER_HEIGHT_SMALL
    : Math.round(
        Math.min(
          HEADER_HEIGHT_LARGE,
          Math.max(HEADER_HEIGHT_SMALL, maxColLabelLen * 5)
        )
      );

  const matrix: Record<string, number> = {};
  cells.forEach((cell) => {
    matrix[`${cell.row}-${cell.column}`] = cell.count;
  });

  // Per-row max for color scaling — highlights the winner in each row
  const rowMax: Record<number, number> = {};
  cells.forEach((cell) => {
    rowMax[cell.row] = Math.max(rowMax[cell.row] ?? 0, cell.count);
  });

  const totalPages = Math.ceil(rowLabels.length / PAGE_SIZE);
  const pagedRowLabels = rowLabels.slice(
    page * PAGE_SIZE,
    (page + 1) * PAGE_SIZE
  );
  const pageOffset = page * PAGE_SIZE;

  const cellSize = Math.max(
    36,
    Math.min(
      64,
      Math.floor(600 / columnLabels.length),
      Math.floor(450 / pagedRowLabels.length)
    )
  );

  const formatPercentage = (count: number): string => {
    if (!totalRespondents || totalRespondents <= 0) {
      return `${count}`;
    }
    const pct = Math.round((count / totalRespondents) * 100);
    return i18n.numberToPercentage(pct, { precision: 0 });
  };

  const handleCellEnter = (
    e: React.MouseEvent,
    rowIdx: number,
    colIdx: number,
    count: number
  ) => {
    const container = containerRef.current as unknown as HTMLElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const target = e.currentTarget.getBoundingClientRect();
    setTooltip({
      count,
      label: i18n.t("dashboardNavigator.chartTooltipHeatmap", {
        line: rowLabels[rowIdx],
        option: columnLabels[colIdx],
      }),
      percentage: formatPercentage(count),
      x: target.left + target.width / 2 - rect.left,
      y: target.top - rect.top,
    });
    setHoveredCell({ row: rowIdx, col: colIdx });
  };

  const handleCellLeave = () => {
    setTooltip(null);
    setHoveredCell(null);
  };

  const gridWidth = LABEL_WIDTH + (cellSize + CELL_GAP) * columnLabels.length;
  // Size to the rows actually on this page — NOT a full `PAGE_SIZE` page — so
  // a section with fewer stimuli than `PAGE_SIZE` doesn't reserve empty rows
  // of dead space under the grid.
  const rowsHeight = pagedRowLabels.length * (cellSize + CELL_GAP);

  return (
    <Box className="w-full py-2 overflow-x-auto overflow-y-visible">
      <Box
        ref={containerRef}
        style={{ minWidth: gridWidth, position: "relative" }}
      >
        <HeatmapTooltip data={tooltip} />

        {/* Column Headers */}
        <Box
          className="flex-row items-end"
          style={{
            height: headerHeight,
            marginLeft: LABEL_WIDTH,
            marginBottom: 6,
          }}
        >
          {columnLabels.map((label, colIdx) => (
            <Box
              key={colIdx}
              className="relative"
              style={{
                width: cellSize + CELL_GAP,
                height: headerHeight,
              }}
            >
              <span
                style={
                  shortLabels
                    ? {
                        bottom: 4,
                        color: "rgb(var(--color-typography-500))",
                        fontSize: 11,
                        fontWeight: 500,
                        left: 0,
                        position: "absolute" as const,
                        textAlign: "center" as const,
                        width: cellSize,
                      }
                    : {
                        WebkitBoxOrient: "vertical" as const,
                        WebkitLineClamp: 2,
                        bottom: 0,
                        color: "rgb(var(--color-typography-500))",
                        display: "-webkit-box",
                        fontSize: 11,
                        fontWeight: 500,
                        left: cellSize / 2,
                        lineHeight: 1.1,
                        overflow: "hidden",
                        position: "absolute" as const,
                        transform: "rotate(-40deg)",
                        transformOrigin: "bottom left",
                        width: headerHeight * 1.4,
                      }
                }
              >
                {label}
              </span>
            </Box>
          ))}
        </Box>

        {/* Rows */}
        <Box style={{ minHeight: rowsHeight }}>
          {pagedRowLabels.map((rowLabel, localIdx) => {
            const rowIdx = pageOffset + localIdx;
            return (
              <Box
                className="flex-row items-center"
                key={rowIdx}
                style={{ marginBottom: CELL_GAP }}
              >
                {/* Row Label */}
                <Box
                  className="justify-center pr-3"
                  style={{ width: LABEL_WIDTH }}
                >
                  <Text
                    className="text-typography-600 text-right"
                    // @ts-expect-error - web-only CSS properties
                    style={{
                      fontSize: 12,
                      fontWeight: "500",
                      lineHeight: 1.1,
                      display: "-webkit-box",
                      overflow: "hidden",
                      WebkitBoxOrient: "vertical" as const,
                      WebkitLineClamp: 2,
                    }}
                  >
                    {rowLabel}
                  </Text>
                </Box>

                {/* Cells */}
                {columnLabels.map((_, colIdx) => {
                  const count = matrix[`${rowIdx}-${colIdx}`] || 0;
                  const maxForRow = rowMax[rowIdx] || 1;
                  const bgColor = getColor(count, maxForRow);
                  const textColor = getTextColor(count, maxForRow);
                  const isHovered =
                    hoveredCell?.row === rowIdx && hoveredCell?.col === colIdx;

                  return (
                    <div
                      key={colIdx}
                      onMouseEnter={(e) =>
                        handleCellEnter(e, rowIdx, colIdx, count)
                      }
                      onMouseLeave={handleCellLeave}
                      style={{
                        alignItems: "center",
                        backgroundColor:
                          count === 0
                            ? "rgb(var(--color-background-50))"
                            : bgColor,
                        border:
                          count === 0
                            ? "1px solid rgb(var(--color-outline-100))"
                            : "none",
                        borderRadius: CELL_RADIUS,
                        cursor: "default",
                        display: "flex",
                        height: cellSize,
                        justifyContent: "center",
                        marginRight: CELL_GAP,
                        position: "relative",
                        width: cellSize,
                      }}
                    >
                      {isHovered && (
                        <div
                          style={{
                            background: "rgba(255, 255, 255, 0.15)",
                            borderRadius: CELL_RADIUS,
                            inset: 0,
                            position: "absolute",
                          }}
                        />
                      )}
                      <span
                        style={{
                          color:
                            count > 0
                              ? textColor
                              : "rgb(var(--color-typography-700))",
                          fontSize: cellSize > 50 ? 13 : 11,
                          fontWeight: count > 0 ? 600 : 400,
                          position: "relative",
                        }}
                      >
                        {formatPercentage(count)}
                      </span>
                    </div>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Pagination */}
      {rowLabels.length > PAGE_SIZE && (
        <HStack className="items-center justify-end gap-2 mt-3 pr-1">
          <Text className="text-xs text-typography-400">
            {i18n.t("questions.matrix.rowsRange", {
              from: page * PAGE_SIZE + 1,
              to: Math.min((page + 1) * PAGE_SIZE, rowLabels.length),
              total: rowLabels.length,
            })}
          </Text>
          <Pressable
            className="p-1"
            disabled={page === 0}
            onPress={() => setPage((p) => p - 1)}
          >
            <ChevronLeft
              color={
                page === 0
                  ? "rgb(var(--color-typography-300))"
                  : "rgb(var(--color-typography-600))"
              }
              size={16}
            />
          </Pressable>
          <Pressable
            className="p-1"
            disabled={page === totalPages - 1}
            onPress={() => setPage((p) => p + 1)}
          >
            <ChevronRight
              color={
                page === totalPages - 1
                  ? "rgb(var(--color-typography-300))"
                  : "rgb(var(--color-typography-600))"
              }
              size={16}
            />
          </Pressable>
        </HStack>
      )}
    </Box>
  );
};

export default Heatmap;
